// Global Types
import type {
  $WithRouter,
} from '@vroskus/library-navigation';
import type {
  $FullChampionship,
} from 'common.data';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';

// Global Components
import * as React from 'react';

// Helpers
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getImageUrl,
  scrollTop,
} from '@vroskus/library-helpers/dist/browser';
import {
  getFullItemUrl,
  getRegionByRegionSeasonId,
} from 'common.helpers/dist/browser';
import {
  getErrorLevel,
  setupParams,
} from '../../../helpers';
import config from '../../../config';

// Components
import Page from '../../layout/Page';
import Loader from '../../layout/Loader';
import BannersSection from '../../sections/ChampionshipPageSections/BannersSection';
import DetailsSection from '../../sections/ChampionshipPageSections/DetailsSection';
import DocumentsSection from '../../sections/ChampionshipPageSections/DocumentsSection';
import RegattasSection from '../../sections/ChampionshipPageSections/RegattasSection';
import ResultsSection from '../../sections/ChampionshipPageSections/ResultsSection';
import SponsorsSection from '../../sections/ChampionshipPageSections/SponsorsSection';
import DescriptionSection from '../../sections/CommonSections/DescriptionSection';

// Services
import Services from '../../../services';

/// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $RedirectAction,
  $SetRegionAction,
} from '../../../redux/modules/main/actions';
import type {
  $Tags,
} from '../../../types';

export type $OwnProps = $WithRouter;

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  redirect: $RedirectAction;
  setRegion: $SetRegionAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

const pageDataNotFoundErrorMessage = 'Page data not found';

type $State = {
  initialised: boolean;
  item: $FullChampionship | null;
};

class ChampionshipPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      initialised: false,
      item: null,
    };
  }

  componentDidMount(): void {
    this.init();
  }

  getTags(): $Tags {
    const {
      main,
    } = this.props;
    const {
      item,
    } = this.state;

    const tags: $Tags = {
      image: undefined,
      title: 'Championship',
      url: undefined,
    };

    if (item !== null) {
      tags.title = item.Name;

      tags.url = getFullItemUrl({
        basePath: '/championship',
        item,
        mainDomain: config.DOMAIN,
        regions: main.regions,
      });

      if (item.Image !== null) {
        tags.image = getImageUrl({
          cdnUrl: config.CDN_URL,
          image: item.Image,
          size: 'max',
        });
      }
    }

    return tags;
  }

  async init(): Promise<void> {
    const {
      main,
      params,
      redirect,
      setRegion,
    } = this.props;

    const {
      param1,
      param2,
    } = params;

    let fallbackParam: string = '';

    try {
      scrollTop();

      const preparedParams = setupParams(
        param1,
        param2,
      );

      if (preparedParams.param2) {
        fallbackParam = preparedParams.param1;
      }

      const item = await Services.Api.getChampionshipPageData(preparedParams);

      if (item !== null) {
        const region = getRegionByRegionSeasonId({
          regions: main.regions,
          RegionSeasonId: item.RegionSeasonId,
        });

        await setRegion({
          RegionId: region.id,
        });

        this.setState({
          initialised: true,
          item,
        });
      } else {
        throw new Error(pageDataNotFoundErrorMessage);
      }
    } catch (error) {
      ErrorLog.exception(
        error as Error,
        getErrorLevel(
          error as Error,
          pageDataNotFoundErrorMessage,
        ),
      );

      redirect({
        pathname: `/${fallbackParam}`,
      });
    }
  }

  render() {
    const {
      initialised,
      item,
    } = this.state;

    return (
      <Page
        id={webPages.championshipPage}
        tags={this.getTags()}
      >
        <Loader loading={!initialised}>
          {(item !== null ? (
            <>
              <DetailsSection item={item} />
              <BannersSection item={item} />
              {item.Content && (
                <DescriptionSection
                  data={item.Content}
                  id={webSections[webPages.championshipPage].description}
                />
              )}
              <SponsorsSection item={item} />
              <RegattasSection item={item} />
              <ResultsSection item={item} />
              <DocumentsSection item={item} />
            </>
          ) : (
            <div />
          ))}
        </Loader>
      </Page>
    );
  }
}

export default ChampionshipPageView;
